/**
 * Location detection utility to determine user region / country (India vs International)
 */

export interface UserLocationResult {
  countryCode: string;
  countryName?: string;
  isIndia: boolean;
  source: string;
}

const STORAGE_KEY = "nakman_user_location";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Check if the browser's timezone belongs to Indian Standard Time (Asia/Kolkata, Asia/Calcutta)
 */
function isIndianTimezone(): boolean {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (
      timeZone === "Asia/Kolkata" ||
      timeZone === "Asia/Calcutta" ||
      timeZone === "IST"
    ) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

/**
 * Synchronous client-side check to determine if user is international
 */
export function isInternationalUserSync(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const intlParam = urlParams.get("intl");
    if (intlParam === "1") return true;
    if (intlParam === "0") return false;

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isIndiaTz = isIndianTimezone();
    if (tz && !isIndiaTz) return true;

    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      return parsed.isIndia === false;
    }
  } catch {
    // ignore
  }
  return false;
}

/**
 * Detects the user's location with multi-tiered fallbacks:
 * 1. Cached result in localStorage / sessionStorage
 * 2. Next.js server-side API `/api/location` (checking Cloudflare / Vercel geo headers)
 * 3. Public IP geolocation APIs (ipapi.co, api.country.is, ipwho.is)
 * 4. Browser Timezone heuristic (Asia/Kolkata)
 */
/**
 * Map known international timezone prefixes to country codes and names
 */
function getCountryFromTimezone(tz: string): { code: string; name: string } {
  if (tz === "Europe/London" || tz === "GMT" || tz === "BST") {
    return { code: "GB", name: "United Kingdom" };
  }
  if (tz.startsWith("America/")) {
    return { code: "US", name: "United States" };
  }
  if (tz.startsWith("Europe/")) {
    return { code: "EU", name: "Europe" };
  }
  if (tz.startsWith("Australia/")) {
    return { code: "AU", name: "Australia" };
  }
  if (tz.startsWith("Asia/Dubai")) {
    return { code: "AE", name: "United Arab Emirates" };
  }
  if (tz.startsWith("Asia/Singapore")) {
    return { code: "SG", name: "Singapore" };
  }
  if (tz.startsWith("Asia/Tokyo")) {
    return { code: "JP", name: "Japan" };
  }
  if (tz.startsWith("Canada/")) {
    return { code: "CA", name: "Canada" };
  }
  return { code: "INTL", name: "International" };
}

/**
 * Detects the user's location with multi-tiered fallbacks:
 * 1. URL test override (?intl=1 / ?intl=0)
 * 2. Browser Timezone check (if timezone is non-Indian like Europe/London, user is international)
 * 3. Cached result in localStorage (validated against current timezone)
 * 4. Next.js server-side API `/api/location/` (checking Cloudflare / Vercel geo headers)
 * 5. Public IP geolocation APIs (api.country.is, ipwho.is)
 */
export async function getUserLocation(): Promise<UserLocationResult> {
  const currentTimeZone =
    typeof window !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "";
  const isIndiaTz = isIndianTimezone();

  // 1. Check for test override query param (e.g. ?intl=1 or ?intl=0)
  if (typeof window !== "undefined") {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const intlParam = urlParams.get("intl");
      if (intlParam === "1") {
        const testResult: UserLocationResult = {
          countryCode: "US",
          countryName: "United States",
          isIndia: false,
          source: "query_override",
        };
        saveLocationToCache(testResult, currentTimeZone);
        return testResult;
      }
      if (intlParam === "0") {
        const testResult: UserLocationResult = {
          countryCode: "IN",
          countryName: "India",
          isIndia: true,
          source: "query_override",
        };
        saveLocationToCache(testResult, currentTimeZone);
        return testResult;
      }
    } catch {
      // ignore
    }
  }

  // 2. High-priority Browser Timezone Check:
  // If the browser timezone is explicitly NON-Indian (such as Europe/London in DevTools Sensors or a foreign device),
  // they are an international customer. We must NOT let an Indian IP from local dev override this.
  if (currentTimeZone && !isIndiaTz) {
    const { code, name } = getCountryFromTimezone(currentTimeZone);
    const result: UserLocationResult = {
      countryCode: code,
      countryName: name,
      isIndia: false,
      source: "browser_timezone",
    };
    saveLocationToCache(result, currentTimeZone);
    return result;
  }

  // 3. Check cached location (only if timezone hasn't changed)
  if (typeof window !== "undefined") {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Only use cache if the cached timezone matches the current browser timezone
        const isTimezoneMatch =
          !parsed.timeZone || parsed.timeZone === currentTimeZone;
        if (
          isTimezoneMatch &&
          parsed.timestamp &&
          Date.now() - parsed.timestamp < CACHE_TTL_MS
        ) {
          return {
            countryCode: parsed.countryCode,
            countryName: parsed.countryName,
            isIndia: parsed.isIndia,
            source: "cache",
          };
        }
      }
    } catch {
      // ignore
    }
  }

  // 4. Try our internal `/api/location/` route (Cloudflare / Vercel geo headers in production)
  try {
    const res = await fetch("/api/location/", { cache: "no-store" });
    if (res.ok) {
      const data = await res.json();
      if (data?.countryCode) {
        const result: UserLocationResult = {
          countryCode: data.countryCode,
          countryName: data.countryName,
          isIndia: data.isIndia ?? data.countryCode === "IN",
          source: data.source || "api/location",
        };
        saveLocationToCache(result, currentTimeZone);
        return result;
      }
    }
  } catch {
    // Continue to external fallbacks
  }

  // 5. Client-side fast IP Geolocation fallback (api.country.is or ipwho.is)
  try {
    const res = await fetch("https://api.country.is/", {
      signal: AbortSignal.timeout(3000),
    });
    if (res.ok) {
      const data = await res.json();
      const code = data?.country?.toUpperCase();
      if (code) {
        const result: UserLocationResult = {
          countryCode: code,
          isIndia: code === "IN",
          source: "country.is",
        };
        saveLocationToCache(result, currentTimeZone);
        return result;
      }
    }
  } catch {
    // Try ipwho.is as alternative
    try {
      const res = await fetch("https://ipwho.is/", {
        signal: AbortSignal.timeout(3000),
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.success && data?.country_code) {
          const code = data.country_code.toUpperCase();
          const result: UserLocationResult = {
            countryCode: code,
            countryName: data.country,
            isIndia: code === "IN",
            source: "ipwho.is",
          };
          saveLocationToCache(result, currentTimeZone);
          return result;
        }
      }
    } catch {
      // Continue to timezone fallback
    }
  }

  // 6. Fallback to Browser Timezone heuristic
  const fallbackResult: UserLocationResult = {
    countryCode: isIndiaTz ? "IN" : "UNKNOWN",
    isIndia: isIndiaTz,
    source: "timezone_fallback",
  };

  saveLocationToCache(fallbackResult, currentTimeZone);
  return fallbackResult;
}

function saveLocationToCache(result: UserLocationResult, timeZone?: string) {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...result,
          timeZone:
            timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          timestamp: Date.now(),
        })
      );
    } catch {
      // ignore
    }
  }
}
